import {pokpokTheme} from '@pokpok/astryx';

/**
 * The merchant accent, threaded onto the theme's own accent tokens.
 *
 * # Why this is an override and not a value in `ThemeStyles`
 *
 * `pokpokTheme` is the source of truth for `--color-accent` and its siblings, and
 * a cross-repo test in astryx holds those to `pokpok_tokens/palette.dart` so the
 * till and this page cannot drift into two different reds. A merchant accent is
 * the one colour that is deliberately *not* the brand red — it is per-tenant data,
 * arriving over the network — so it cannot live in that theme. It is emitted as a
 * second `:root {}` block **after** the theme (see `AccentStyles`, mounted below
 * `ThemeStyles`), overriding exactly the accent tokens and nothing else. A shop
 * with no branding emits no block at all and keeps Chili Red — the default look
 * is never broken.
 *
 * # The contrast guard, because the accent is arbitrary
 *
 * A merchant can save any colour, including a pale one that white button text
 * disappears on, or one so light it is unreadable as text on the Sticky Rice
 * ground. Two guards, both computed from the accent's own luminance:
 *
 *   * **Text ON the accent fill** (the primary button label) gets a *computed*
 *     ink — white or near-black, whichever has more contrast against the accent —
 *     bound to the theme's own `--color-on-dark` / `--color-on-light`, never a raw
 *     hex. So a pale-yellow accent gets dark text and a deep-navy one gets white.
 *   * **The accent used AS text** (`--color-text-accent`, `--color-icon-accent`,
 *     on the light ground) is only repainted when the accent clears 4.5:1 against
 *     the ground. A pale accent that would be illegible as text is left as the
 *     default red — the fill still takes the merchant colour, the text does not.
 */

/** Light-surface token values, read from astryx so this never drifts from it. */
const LIGHT = pokpokTheme.variables('light');
const BODY_BG = LIGHT['--color-background-body'] ?? '#F7F3EE';
const INK = LIGHT['--color-text-primary'] ?? '#1A1D23';
const WHITE = LIGHT['--color-on-dark'] ?? '#FFFFFF';

/** WCAG AA for normal text. The accent-as-text guard threshold. */
const TEXT_CONTRAST_MIN = 4.5;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/**
 * Parse `#rgb` or `#rrggbb` to channels, or `null` for anything else.
 *
 * The contract promises `#rrggbb`; the short form is accepted too because it is a
 * valid CSS colour a merchant tool might store, and everything else — a named
 * colour, an `rgb()` string, an empty value, a stray script — is refused. Refusing
 * is what keeps an arbitrary string out of the stylesheet.
 */
export function parseHexColor(value: string | null | undefined): Rgb | null {
  if (typeof value !== 'string') return null;
  const hex = value.trim().toLowerCase();
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(hex);
  if (short) {
    return {
      r: parseInt(short[1]! + short[1]!, 16),
      g: parseInt(short[2]! + short[2]!, 16),
      b: parseInt(short[3]! + short[3]!, 16),
    };
  }
  const long = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/.exec(hex);
  if (long) {
    return {
      r: parseInt(long[1]!, 16),
      g: parseInt(long[2]!, 16),
      b: parseInt(long[3]!, 16),
    };
  }
  return null;
}

/** Canonical `#rrggbb` lowercase for a valid colour, or `null`. */
export function normalizeHexColor(value: string | null | undefined): string | null {
  const rgb = parseHexColor(value);
  if (!rgb) return null;
  const hx = (n: number) => n.toString(16).padStart(2, '0');
  return `#${hx(rgb.r)}${hx(rgb.g)}${hx(rgb.b)}`;
}

/** WCAG relative luminance in [0,1]. */
export function relativeLuminance(rgb: Rgb): number {
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/** WCAG contrast ratio between two colours, 1..21. Order-independent. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * The theme var to use for ink sitting on top of the accent fill — whichever of
 * the theme's on-dark / on-light inks has more contrast against the accent.
 */
export function onAccentTokenFor(accent: Rgb): string {
  const white = parseHexColor(WHITE)!;
  const ink = parseHexColor(INK)!;
  return contrastRatio(accent, white) >= contrastRatio(accent, ink)
    ? 'var(--color-on-dark)'
    : 'var(--color-on-light)';
}

/**
 * The `:root {}` override block for a merchant accent, or `null` when the accent
 * is missing or not a colour (fall back to the default theme).
 */
export function accentOverrideCss(accent: string | null | undefined): string | null {
  const hex = normalizeHexColor(accent);
  const rgb = parseHexColor(hex);
  if (!hex || !rgb) return null;

  const lines = [
    `  --color-accent: ${hex};`,
    `  --color-on-accent: ${onAccentTokenFor(rgb)};`,
  ];

  // Only let the accent become text/icon ink if it is legible on the ground.
  const bg = parseHexColor(BODY_BG)!;
  if (contrastRatio(rgb, bg) >= TEXT_CONTRAST_MIN) {
    lines.push(`  --color-text-accent: ${hex};`);
    lines.push(`  --color-icon-accent: ${hex};`);
  }

  return `:root {\n${lines.join('\n')}\n}`;
}
